import Axios from 'axios'
import store from '@/store'
import { sleep } from '@/util/common'
import { API_RELAY_PATH } from '@/util/constants'
import router from '@/router'

const ERROR_MESSAGE_TOKEN_NULL = 'トークンがないためリクエストはキャンセルされました。'

let occurTimeoutFlag = false

/**
 * 共通設定
 */
export const defaultSetting = {
  baseURL: process.env.VUE_APP_LC_API_URL ?? 'https://www.sn-hoki.co.jp/',
  withCredentials: true,
  headers: {
    'Contet-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    'Access-Control-Allow-Origin': '*'
  },
  // timeout: 10,
  auth: (process.env.VUE_APP_LC_API_URL_BASIC_USER && process.env.VUE_APP_LC_API_URL_BASIC_PASS) ? {
    username: process.env.VUE_APP_LC_API_URL_BASIC_USER,
    password: process.env.VUE_APP_LC_API_URL_BASIC_PASS
  } : null
}

/**
 * タイムアウト時の処理
 */
const processTimeout = () => {

  // タイムアウト発生フラグ
  occurTimeoutFlag = true
  // トークンクリア
  const token = null
  const refreshToken = null
  store.dispatch('auth/setToken', { token, refreshToken })

  // ログイン画面に強制遷移した時にタイムアウトのNoticeを表示するフラグを立てる
  store.dispatch('login/setIsTimeoutMessageShown', true)

  // タイムアウト後のログイン時の遷移先を設定する。
  if ((store.state.common.fromName === 'PCQADetail' || store.state.common.fromName === 'PCKnowledgeDetail') && store.state.common.toName === 'PCLogin') {
    // PC版の場合
    store.dispatch('login/setDestinationURI', store.state.common.fromPath)
  } else if ((store.state.common.fromName === 'SPQADetail' || store.state.common.fromName === 'SPKnowledgeDetail') && store.state.common.toName === 'SPLogin') {
    // SP版の場合
    store.dispatch('login/setDestinationURI', store.state.common.fromPath)
  } else if ((store.state.common.fromName === 'SPLogin' || store.state.common.fromName === 'PCLogin') && (store.state.common.toName === 'SPLogin' || store.state.common.toName === 'PCLogin')) {
    // ブラウザによってSP版のログイン画面とPC版のログイン画面をbeforeEachにより実行されていることがあるため、その際は保存もクリアもされないように何もしない
  } else {
    // 指定の画面以外は、ログイン後の画面ではダッシュボード画面に遷移させる。
    store.dispatch('login/setDestinationURI', '')
  }

  // ログイン画面に強制遷移
  router.push({ path: '/pc/login' }).catch(() => { })
}

/**
 * リクエストデータの共通処理
 * @param {response} response リクエストデータ
 * @param {boolean} isErrorStatusDealed エラー処理を実施するか
 * @return 加工したレスポンスデータ
 */
export const interceptRequest = async (request, isErrorStatusDealed) => {
  if (isErrorStatusDealed) {
    if (store.state.common.isRequireAuth) {
      // 認証が必要なページの場合
      // トークンnullチェック
      if (typeof request.data !== 'undefined' && typeof request.data.token !== 'undefined') {
        // tokenが設定されているリクエストの場合
        if (!request.data.token) {
          // トークンがnullの場合
          throw new Axios.Cancel(ERROR_MESSAGE_TOKEN_NULL)
        }
      }
    }
  }
  return request
}

/**
 * レスポンスデータの共通処理
 * @param {response} response レスポンスデータ
 * @param {boolean} isErrorStatusDealed ステータスが0以外の場合(異常系コード)に対してエラー処理を実施するか
 * @return 加工したレスポンスデータ
 */
export const processResponse = async (response, isErrorStatusDealed) => {

  // APIコール時にタイムスタンプを更新
  store.dispatch('auth/apiCall')
  if (response.status === 200) {
    // HTTPステータスが200

    if (
      typeof response.data === 'undefined' || 
      typeof response.data.status === 'undefined'
    ) {
      // レスポンスにデータが含まれない
      return response
    } else if (response.data.status === 401) {
      // APIステータスが401

      // tokenの有効期限切れの場合
      const isTimeOut = store.getters['auth/isTimeOut']
      if (isTimeOut) {
        // タイムアウトの場合

        // タイムアウト時の処理
        processTimeout()
        
        return response
      }
      // トークンリフレッシュ中の場合は、待機
      if (store.getters['auth/isRefresh']) {
        return reRequest(response)
      } else {
        tokenRefresh(response)
        return reRequest(response)
      }
    } else if (response.data.status === 400) {
      // APIステータスが400
      return reRequest(response)
      // tokenリフレッシュ中にリクエストしたAPIの救済
      // return reAxios.request(response.config)
    } else if (response.data.status === 0 && response.data.error_message === '') {
      // リクエスト成功
      return response
    } else if (isErrorStatusDealed && response.data.status !== 0 && response.data.error_message !== '') {
      // エラーコードが0以外（内部エラー）の場合

      // 遷移先名
      const toName = store.state.common.toName

      let title = 'エラー'
      let text = response.data.error_message

      if (toName === 'PCQADetail' || toName === 'SPQADetail') {
        if (response.data.status === 403) {
          try {
            const json = response.config.data
            const data = JSON.parse(json)
            const apiPath = data._path
            if (apiPath === 'QA/009' || apiPath === 'QA/010') {
              // 書籍情報取得API(QA-009)またはQA詳細取得API(QA-010)の場合
              // QA詳細画面で、契約していない書籍を表示しようとした場合のダイアログ文言
              // タイトル：閲覧できません　メッセージ：対象情報は閲覧できません。
              store.dispatch('common/setDecidedNoticeTitle', '閲覧できません')
              store.dispatch('common/setDecidedNoticeMessage', response.data.error_message)
            }
          } catch (e) {
          }
        }
      }

      if (store.state.common.decidedNoticeTitle !== '') {
        // 確定したダイアログタイトルが設定されている場合は、それを表示する
        title = store.state.common.decidedNoticeTitle
      }
      if (store.state.common.decidedNoticeMessage !== '') {
        // 確定したダイアログメッセージが設定されている場合は、それを表示する
        text = store.state.common.decidedNoticeMessage
      }

      store.dispatch('common/showNotice', { title: title, text: text })
      return response
    } else {
      // いずれにも当てはまらない場合
      return response
    }
  } else {
    return response
  }
}

const tokenRefresh = async (response) => {

  store.dispatch('auth/beginRefresh')

  const reAxios = Axios.create(defaultSetting)

  return reAxios
    .post(API_RELAY_PATH, {
      _path: 'CM/006',
      token: store.state.auth.token,
      refresh_token: store.state.auth.refreshToken
    })
    .then(refreshResponse => {
      const { token, refresh_token: refreshToken } = refreshResponse.data
      // tokenが返却された場合は、値をセット
      if (refreshResponse.status === 200 && token && refreshToken) {
        store.dispatch('auth/setToken', { token, refreshToken })
      } else {
        throw refreshResponse
      }
    })
    .catch(e => {
      // tokenの更新に失敗した場合の処理

      // タイムアウト時の処理
      processTimeout()

    })
    .finally(() => {
      store.dispatch('auth/completeRefresh')
    })
}


/**
 * tokenが更新されている場合は、tokenを最新の値に変更し再リクエストする
 * @param {object} config 
 */
const reRequest = async (response) => {

  await waitRefresh()

  const config = response.config
  // tokenが更新されている場合は、tokenを最新の値に変更し再リクエスト
  if (isTokenUpdate(config)) {
    const reAxios = Axios.create(defaultSetting)
    // リクエストを送るときの共通処理
    reAxios.interceptors.request.use((request) => {
      return interceptRequest(request)
    })
    const { data } = config
    const dataJson = JSON.parse(data)
    dataJson.token = store.getters['auth/token']
    config.data = JSON.stringify(dataJson)
    return reAxios.request(config)
  } else {
    // 複数回同じページへ遷移する可能性が高いため
    // router.push({ path: '/pc/login' }).catch(() => { })

    return response
  }
}

/**
 * 任意の値がtrueになるまで待機する
 * @param {boolean} state 
 */
const waitRefresh = async () => {
  return new Promise(resolve => {
    const interval = setInterval(() => {
      // リフレッシュ処理が完了していれば返却
      if (!store.getters['auth/isRefresh']) {
        clearInterval(interval)
        resolve()
      // } else {
      //   console.log('wait until')
      }
    }, 100)
  })
}


/**
 * リクエスト前後でtokenが更新されているか否かを確認
 * @param {object} config 
 */
const isTokenUpdate = (config) => {
  const { data } = config
  try {
    const dataJson = JSON.parse(data)
    return dataJson.token !== store.getters['auth/token']
  } catch (e) {
    return false
  }
}

/**
 * エラーデータの共通処理
 * @param {response} response レスポンスデータ
 * @param {boolean} isErrorStatusJudged ステータスが0以外の場合(異常系コード)に対してエラー処理を実施する
 */
export const processError = (error) => {

  if (typeof error.message !== 'undefined' && error.message === ERROR_MESSAGE_TOKEN_NULL) {
    // トークンがnullの場合
    // ※CM/006でリフレッシュトークン時に取得できない場合は、token:nullが設定されているためここに来る。
    processTimeout()
  } else {
    const isTimeOut = store.getters['auth/isTimeOut']
    const isTimeoutOfAxios = error.code === 'ECONNABORTED'
    const isNoResponse = !error.response
    if (isTimeOut) {
      // タイムアウトしている場合は、ステータス関係なく、タイムアウトにする
      processTimeout()
    } else if (isTimeoutOfAxios) {
      // タイムアウト時の処理
      processTimeout()
    } else if (isNoResponse) {
      // レスポンスなし
      store.dispatch('common/showError', { type: 'NoResponse' })
    } else if (error.response) {
      // それ以外のエラー
      const statusCode = error.response.status ?? null
      const statusText = error.response.statusText ?? null
      store.dispatch('common/showError', { type: 'StatusError', code: statusCode, text: statusText })
    }
  }
  throw error
}
